// V2.9 Inputs: the values the instructions tell the agent to collect, one row per value (name + the sentence it came from).
// ponytail: English "ask/collect … their A, B and C" rules give instant candidates; an LLM pass at publish replaces them when precision matters.
const RECORD_AS = /\b(?:record|store|save)(?:\s+(?:it|this|that))?\s+as\s+([A-Za-z][A-Za-z0-9_]*)/gi
const ASK_FOR =
  /\b(?:ask(?:\s+(?:the\s+)?(?:user|caller|customer))?\s+for|collect|capture|get|obtain|request|take\s+down)\s+(?:the\s+|a\s+|an\s+)?(?:(?:user|caller|customer)['’]?s?\s+|their\s+|your\s+)?(?:best\s+|current\s+|preferred\s+)?([a-z][a-z0-9' ,]*?)(?=\s*(?:\(|\.|;|:|\bto\b|\bfor\b|\bif\b|\bso\b|\bwhich\b|\bthat\b|$))/gim
const NEGATED =
  /\b(?:do not|don't|never|no need to|without)\s+(?:ask|collect|capture|get|obtain|request|take)/i
// 'back' rides in as a particle ("get back to the customer"), so it is a stop word like the articles.
const STOP = new Set([
  'and',
  'or',
  'a',
  'an',
  'the',
  'their',
  'your',
  'best',
  'current',
  'number',
  'back',
])

const snake = (phrase: string) =>
  phrase
    .trim()
    .toLowerCase()
    .replace(/['’]s\b/g, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !STOP.has(w))
    .slice(0, 3)
    .join('_')

export type InputCandidate = { name: string; sentence: string }

export const inputsFromPrompt = (text: string): InputCandidate[] => {
  const out: InputCandidate[] = []
  const push = (name: string, sentence: string) => {
    // The name has to be a legal Input name (leading letter, then letters/digits/underscores).
    if (name.length > 1 && /^[a-z][a-z0-9_]*$/.test(name) && !out.some((c) => c.name === name))
      out.push({ name, sentence: sentence.trim() })
  }
  for (const sentence of (text ?? '').split(/(?<=[.;!?])\s+|\n+/)) {
    // a "do not ask" negates only its own clause
    const clauses = sentence.split(/\bbut\b|;/).filter((c) => !NEGATED.test(c))
    // "record as" names the sentence's value outright and silences every guess in it,
    // whatever its clauses look like; that row is described by the whole sentence.
    const explicit = clauses.flatMap((c) =>
      Array.from(c.matchAll(RECORD_AS), (m) => m[1].toLowerCase()),
    )
    if (explicit.length) {
      for (const name of explicit) push(name, sentence)
      continue
    }
    // a guessed row is described by the clause it came from, so a negated sibling clause
    // never ends up as the description of a value the agent does collect
    for (const clause of clauses) {
      for (const m of clause.matchAll(ASK_FOR)) {
        // "their name, phone number, and email" → one input per list item; "phone number" keeps "phone".
        // "a morning or evening slot" is ONE value, so "or" is not a separator.
        for (const item of m[1].split(/,|\band\b/))
          push(snake(item.replace(/\bnumber\b/, '')), clause)
      }
    }
  }
  return out
}

export type InputRow = {
  variable_name: string
  variable_instructions: string
  variable_format_instructions: string
  variable_type: string
  variable_enum_values: string[]
  configure_data_format: boolean
  id: string
}

// Adds a row for every input the instructions mention that the list lacks; existing rows and their edited descriptions stay.
export const withDetectedInputs = <T extends { variable_name?: string | null }>(
  instructions: string,
  rows: T[] | null | undefined,
): (T | InputRow)[] => {
  const have = new Set((rows ?? []).map((r) => r.variable_name))
  const added = inputsFromPrompt(instructions)
    .filter((c) => !have.has(c.name))
    .map((c) => ({
      variable_name: c.name,
      variable_instructions: c.sentence,
      variable_format_instructions: '',
      variable_type: 'string',
      variable_enum_values: [],
      configure_data_format: false,
      id: crypto.randomUUID(),
    }))
  return [...(rows ?? []), ...added]
}
